import SettingsShell from "../../components/SettingsShell";
import {
  ProfileInfoPanel, BusinessDetailsPanel, PaymentBillingPanel,
  ShippingAddressesPanel, NotificationsPanel, TeamMembersPanel,
  SecurityPanel,
} from "../../components/SettingsPanels";
import BuyerDeveloperPortal from "./BuyerDeveloperPortal";
import { PaymentApprovalsPanel } from "../../components/PaymentApprovalsPanel";
import BuyerPublicProfilePanel from "../../components/BuyerPublicProfilePanel";
import PlanBillingPanel from "../../components/PlanBillingPanel";
import AutoDebitAuthorizationCard from "../../components/AutoDebitAuthorizationCard";
import KycVerificationPanel from "../../components/KycVerificationPanel";
import MyTradeScorePanel from "../../components/MyTradeScorePanel";
import { useAuth } from "../../context/AuthContext";
import {
  User, Building2, CreditCard, Truck, Plug, Bell, Users, Shield, ShieldCheck, Sparkles, BarChart3, Store,
} from "lucide-react";

/* Buyer Settings — subset of the seller shell, scoped to buyer needs. */
export default function BuyerSettings() {
  const { user } = useAuth();
  // Default-true for legacy users (no team_flags = solo owner).
  const flags = user?.team_flags || {};
  const canBanking  = flags.can_banking  !== false;  // manage cards/banks
  const canMoney    = flags.can_money    !== false;  // pay invoices etc. (admin-allowed)
  const canSettings = flags.can_settings !== false;  // change addresses / KYB
  // Co-approval settings are OWNER-ONLY — financial governance lives
  // strictly with the account owner. Admins, operators, viewers shouldn't
  // even see the tab.
  const isOwner = (user?.team_role || "owner") === "owner";
  const tabs = [
    { id: "profile",    label: "Profile info",       icon: User,       group: "Account",    render: () => <ProfileInfoPanel/> },
    ...(canSettings ? [
      { id: "business",   label: "Business details",   icon: Building2,                       render: () => <BusinessDetailsPanel/> },
      // Identity & bank verification — required before a buyer can move
      // money on Verqo (wallet topup, invoice pay, etc.). Same gated step
      // as on the seller side; the backend `/onboarding/kyc/*` endpoints
      // are role-agnostic so the panel works as-is for buyers.
      { id: "kyc",        label: "Identity & bank",    icon: ShieldCheck,                     render: () => <KycVerificationPanel/> },
    ] : []),
    ...(canSettings ? [
      // Jun 2026 — Public profile lets the buyer self-report
      // identity + brand-fit signals that sellers see in their drawer.
      // Iter 246 · icon aligned with seller Settings ("Store") — the two
      // portals now use identical icons for every shared Settings tab.
      { id: "public-profile", label: "Public profile", icon: Store, render: () => <BuyerPublicProfilePanel/> },
      // Iter 160 — Verqo Trade Score (BETA). Buyer self-view + dispute + opt-out.
      { id: "trade-score", label: "Trade Score", icon: BarChart3, render: () => <MyTradeScorePanel/> },
    ] : []),

    ...(canSettings ? [
      { id: "shipping",   label: "Shipping addresses", icon: Truck,      group: "Operations", render: () => <ShippingAddressesPanel labelSingular="address" titleLabel="Shipping addresses" descLabel="Default delivery address and any alternate ones — used when placing orders."/> },
    ] : []),

    ...(canBanking || canMoney ? [
      // Payment & billing surface — owners get full add/remove banks; admins
      // can still view balances + pay/withdraw. The panel itself decides
      // what's editable based on `can_banking`.
      { id: "payment",    label: "Payment & billing",  icon: CreditCard, group: "Finance",    render: () => <PaymentBillingPanel/> },
      { id: "bank-auto-debit", label: "Bank auto-debit",  icon: CreditCard, group: "Finance", render: () => <AutoDebitAuthorizationCard role="buyer"/> },
    ] : []),
    ...(isOwner ? [
      { id: "payment-approvals", label: "Payment approvals", icon: ShieldCheck, group: "Finance", render: () => <PaymentApprovalsPanel/> },
      // Plan & Billing — buyer-side renders the "always free" info card.
      { id: "plan-billing", label: "Plan & Billing", icon: Sparkles, group: "Finance", render: () => <PlanBillingPanel role="buyer"/> },
    ] : []),

    { id: "integrations", label: "Integrations & API", icon: Plug,     group: "Automation", render: () => <BuyerDeveloperPortal embedded/> },
    { id: "notifications", label: "Notifications",   icon: Bell,                            render: () => <NotificationsPanel/> },

    { id: "team",       label: "Team members",       icon: Users,      group: "Access",     render: () => <TeamMembersPanel/> },
    { id: "security",   label: "Security",           icon: Shield,                          render: () => <SecurityPanel/> },
  ];
  return <SettingsShell title="Settings" subtitle="Everything about your account, business and buyer preferences — in one place." tabs={tabs} />;
}
